'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import Link from 'next/link';

export default function DataFlowPage() {
  useEffect(() => {
    // Initialize Mermaid when component mounts
    if (typeof window !== 'undefined' && (window as any).mermaid) {
      (window as any).mermaid.initialize({
        startOnLoad: true,
        theme: 'base',
        themeVariables: {
          primaryColor: '#f1f5f9',
          primaryTextColor: '#0f172a',
          primaryBorderColor: '#cbd5e1',
          lineColor: '#64748b',
          secondaryColor: '#f8fafc',
          tertiaryColor: '#ffffff'
        },
        flowchart: {
          htmlLabels: true,
          curve: 'basis',
          nodeSpacing: 50,
          rankSpacing: 60
        },
        securityLevel: 'loose'
      });
      
      // Force re-render of mermaid diagrams
      (window as any).mermaid.contentLoaded();
    }

    // Add drag-to-scroll functionality
    const container = document.getElementById('diagram-container');
    if (!container) return;

    let isDown = false;
    let startX: number;
    let startY: number;
    let scrollLeft: number;
    let scrollTop: number;

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      container.classList.add('cursor-grabbing');
      startX = e.pageX - container.offsetLeft;
      startY = e.pageY - container.offsetTop;
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
    };

    const handleMouseLeave = () => {
      isDown = false;
      container.classList.remove('cursor-grabbing');
    };

    const handleMouseUp = () => {
      isDown = false;
      container.classList.remove('cursor-grabbing');
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const y = e.pageY - container.offsetTop;
      const walkX = (x - startX) * 2;
      const walkY = (y - startY) * 2;
      container.scrollLeft = scrollLeft - walkX;
      container.scrollTop = scrollTop - walkY;
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <>
      <Script 
        src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"
        onLoad={() => {
          if ((window as any).mermaid) {
            (window as any).mermaid.contentLoaded();
          }
        }}
      />
      
      <div className="flex flex-col h-full">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center shadow-sm">
          <div>
            <div className="flex items-center gap-3">
              <Link 
                href="/dashboard" 
                className="text-slate-400 hover:text-slate-600 transition-colors"
                title="Back to Dashboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Calibrate Dashboard Data Architecture
              </h1>
            </div>
            <p className="text-sm text-slate-500 mt-1 ml-8">Project: yc-partners | High-Level Data Flow Integration Map</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-200">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              System Map Updated
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6 flex flex-col gap-4 overflow-hidden relative bg-slate-50">
          
          {/* Controls */}
          <div className="flex justify-between items-center text-sm text-slate-600 px-2">
            <p className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <strong>Tip:</strong> Click and drag to pan around the diagram. Scroll to zoom. Diagram illustrates full data lifecycle.
            </p>
            <Link 
              href="/dashboard"
              className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Back to Dashboard
            </Link>
          </div>

          {/* Diagram Container */}
          <div 
            className="flex-1 overflow-auto bg-white rounded-lg shadow-lg p-8 cursor-grab active:cursor-grabbing" 
            id="diagram-container"
          >
            <pre className="mermaid text-center">
{`flowchart TD
    %% -- STYLES --
    classDef source fill:#e0f2fe,stroke:#0284c7,stroke-width:2px,color:#0c4a6e,font-weight:bold
    classDef etl fill:#ffedd5,stroke:#ea580c,stroke-width:2px,color:#7c2d12,stroke-dasharray: 4 4
    classDef bqDataset fill:#dcfce7,stroke:#16a34a,stroke-width:2px,color:#14532d
    classDef bqTable fill:#ffffff,stroke:#86efac,stroke-width:1px,color:#166534
    classDef bqView fill:#fef08a,stroke:#ca8a04,stroke-width:2px,color:#713f12,stroke-dasharray: 3 3
    classDef auth fill:#f1f5f9,stroke:#64748b,stroke-width:1px,color:#334155,stroke-dasharray: 2 2
    classDef api fill:#f3e8ff,stroke:#9333ea,stroke-width:2px,color:#4c1d95,font-weight:bold
    classDef dashboard fill:#ccfbf1,stroke:#0d9488,stroke-width:2px,color:#115e59,font-weight:bold

    %% -- LAYER 1: SOURCE SYSTEMS --
    subgraph Sources ["1️⃣ SOURCE SYSTEMS (External & Internal)"]
        direction LR
        L["📦 Logiwa WMS<br/>(15 min / API Polling)"]:::source
        TS["🚢 TechShip<br/>(Daily / Batch Import)"]:::source
        TT["📍 TechTrack<br/>(Real-time / Webhooks)"]:::source
        G["👥 Gusto HR<br/>(Weekly / API Sync)"]:::source
        B["💰 Billing System<br/>(Daily / DB Export)"]:::source
    end

    %% -- LAYER 2: ETL --
    subgraph Pipeline ["2️⃣ DATA INTEGRATION PIPELINE"]
        direction LR
        CS["⏱️ Cloud Scheduler<br/>(Triggers Jobs)"]:::etl
        CR["🐍 Python ETL Scripts<br/>(Cloud Run)"]:::etl
        CS -.-> CR
    end

    L -->|"Orders, Inv, Tasks"| CR
    TS -->|"Shipping Costs, Margins"| CR
    TT -->|"Delivery SLAs, Transit"| CR
    G -->|"Payrolls, Time Entries"| CR
    B -->|"Invoices, Catalog"| CR

    %% -- LAYER 3: BIGQUERY --
    subgraph BigQuery ["3️⃣ GOOGLE CLOUD BIGQUERY (Project: yc-partners)"]
        direction TB
        
        subgraph Datasets ["🗄️ Raw Datasets (~816k Total Records)"]
            direction LR
            
            subgraph DS_L ["yc-partners.logiwa (101 tables)"]
                direction TB
                T_SO["shipment_orders"]:::bqTable
                T_SOD["shipment_order_details"]:::bqTable
                T_SOP["shipment_order_packages"]:::bqTable
                T_WT["warehouse_tasks"]:::bqTable
                T_IL["inventory_locations"]:::bqTable
            end
            
            subgraph DS_TS ["yc-partners.techship (12 tables)"]
                direction TB
                T_TSS["techship_shipments"]:::bqTable
                T_CR["carrier_rates"]:::bqTable
                T_SA["shipment_audits"]:::bqTable
            end

            subgraph DS_TT ["yc-partners.techtrack (8 tables)"]
                direction TB
                T_TE["tracking_events"]:::bqTable
                T_DP["delivery_performance"]:::bqTable
            end

            subgraph DS_G ["yc-partners.gusto (15 tables)"]
                direction TB
                T_P["payrolls"]:::bqTable
                T_TE2["time_entries"]:::bqTable
            end

            subgraph DS_B ["yc-partners.billing (14 tables)"]
                direction TB
                T_I["invoices"]:::bqTable
                T_ILI["invoice_line_items"]:::bqTable
            end
        end

        subgraph Views ["📊 Aggregation & Views Layer"]
            direction LR
            V1["v_daily_income_statement<br/>(Revenue - Freight - Labor)"]:::bqView
            V2["v_client_rankings<br/>(Rev, Vol, Profit)"]:::bqView
            V3["v_carrier_performance<br/>(Cost, SLA, Margin)"]:::bqView
            V4["v_warehouse_kpi_daily_summary<br/>(Productivity, Orders)"]:::bqView
        end

        DS_L & DS_TS & DS_TT & DS_G & DS_B -->|"SQL Transformation"| Views
    end

    CR -->|"Load Raw Data"| Datasets

    %% -- LAYER 4: API BACKEND --
    subgraph Backend ["4️⃣ API & APPLICATION LAYER"]
        direction TB
        SA["🔐 Service Account Auth<br/>(sergio@yc-partners)"]:::auth
        API["⚙️ Flask Backend API<br/>(enhanced_api.py / demo_api.py)"]:::api
        
        subgraph Endpoints ["Active Endpoints"]
            direction LR
            EP1["/api/dashboard/summary"]:::bqTable
            EP2["/api/dashboard/clients"]:::bqTable
            EP3["/api/dashboard/income-statement"]:::bqTable
            EP4["/api/analytics/orders"]:::bqTable
        end
        
        API --- Endpoints
    end

    Views -->|"SQL Query Execution"| SA
    SA --> API

    %% -- LAYER 5: FRONTEND DASHBOARD --
    subgraph Frontend ["5️⃣ CALIBRATE DASHBOARD (Next.js - calibrate.sgarza.com)"]
        direction LR
        UI1["📦 Orders Dashboard<br/>(Volume, AOV, Weight)"]:::dashboard
        UI2["👥 Clients Dashboard<br/>(Profitability, Frequency)"]:::dashboard
        UI3["🚚 Freight Dashboard<br/>(Margins, Costs by Zone)"]:::dashboard
        UI4["🏭 Warehouse Dashboard<br/>(Labor/Order, Productivity)"]:::dashboard
        UI5["💵 Income Statement<br/>(Gross/Net Margin, COGS)"]:::dashboard
    end

    API -->|"JSON Payload over HTTPS<br/>(< 1s Response Time)"| Frontend

    %% Highlight specific complex calculations
    UI5 -.->|"Calculated via: Billing Rev<br/>- TechShip Costs<br/>- Gusto Labor"| V1`}
            </pre>
          </div>
        </main>
      </div>

      <style jsx global>{`
        .mermaid-container {
          width: 100%;
          height: calc(100vh - 120px);
          overflow: auto;
          background-color: #ffffff;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .mermaid svg {
          max-width: none !important;
          margin: 0 auto;
          display: block;
        }
      `}</style>
    </>
  );
}
